// frontend/src/components/mice/ExcelImport.js
// Self-contained Excel import component.
// Manages its own import-specific state; notifies parent via callbacks on completion.

import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useToast } from '../../hooks/useToast';
import { DataService } from '../../services/DataService';

export function ExcelImport({ onImportComplete, onCancel }) {
  const toast = useToast();
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  // `error` is validation only — "pick a file" is form UX and belongs next to
  // the input. The outcome of the upload itself is an action result and goes
  // to a toast; its per-row detail stays below in `importResults`, which is a
  // report the user needs to read at their own pace, not a flash message.
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    setImportFile(e.target.files[0] || null);
    setImportResults(null);
    setError('');
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setImportResults(null);
    setImportLoading(true);

    if (!importFile) {
      setError('Please select an Excel file to import.');
      setImportLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      // No headers: the auth interceptor stamps the token, and axios derives
      // the multipart Content-Type (with its boundary) from the FormData —
      // hand-setting it produced a boundary-less header.
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/mice/import`, formData);
      await DataService.sync();

      setImportResults(response.data);
      const failed = response.data.failed_rows_count || 0;
      // A partial import is not a success. Warn so the user looks at the
      // per-row report instead of walking away from a green banner.
      if (failed > 0) {
        toast.warn(response.data.message);
      } else {
        toast.success(response.data.message);
      }
      onImportComplete && onImportComplete();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to import file.';
      const importErrors = err.response?.data?.import_errors || [];
      toast.error(errorMessage);
      setImportResults({
        message: errorMessage,
        failed_rows_count: importErrors.length,
        import_errors: importErrors,
      });
      console.error('Import error:', err);
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="add-mouse-section">
      <h2>Import Mice from Excel (.xlsx)</h2>
      <form onSubmit={handleImportSubmit} className="add-mouse-form">
        <div className="form-group">
          <label htmlFor="excelFile">Select Excel File:</label>
          <input
            type="file"
            id="excelFile"
            accept=".xlsx"
            onChange={handleFileChange}
            ref={fileInputRef}
            required
          />
        </div>

        {importLoading && <div className="loading">Importing... Please wait.</div>}
        {error && <div className="error">{error}</div>}

        {importResults && (
          <div
            className={`import-results ${importResults.failed_rows_count > 0 ? 'error' : 'success'}`}
            style={{
              marginBottom: '20px', padding: '15px', borderRadius: '8px',
              border: `1px solid ${importResults.failed_rows_count > 0 ? '#dc3545' : '#28a745'}`,
              background: `${importResults.failed_rows_count > 0 ? '#f8d7da' : '#d4edda'}`,
            }}
          >
            <h3>Import Results:</h3>
            <p>{importResults.message}</p>
            {importResults.failed_rows_count > 0 && (
              <div>
                <p><strong>{importResults.failed_rows_count} row(s) failed to import:</strong></p>
                <ul style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ccc', padding: '10px', borderRadius: '5px', background: '#fff' }}>
                  {importResults.import_errors.map((err, index) => (
                    <li key={index} style={{ marginBottom: '5px', color: '#dc3545' }}>
                      Row {err.row}: {err.message}
                      {err.data && (
                        <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
                          (Data: {JSON.stringify(err.data)})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="submit" className="primary-button" style={{ flex: 1 }} disabled={importLoading}>
            {importLoading ? 'Importing...' : 'Upload & Import'}
          </button>
          <button type="button" onClick={onCancel} className="secondary-button" style={{ flex: 1 }} disabled={importLoading}>
            Cancel
          </button>
        </div>
      </form>

      <div style={{ marginTop: '20px', fontSize: '0.8rem', color: '#555' }}>
        <p><strong>Expected Excel Columns (Case-sensitive):</strong></p>
        <ul>
          <li><code>Physical Tag</code> (Optional)</li>
          <li><code>Sex</code> (Required: 'Male' or 'Female')</li>
          <li><code>Date of Birth</code> (Required: YYYY-MM-DD or Excel date format)</li>
          <li><code>Genotype</code> (Required: e.g., 'Locus(Allele)', 'Locus(Allele), Locus2(Allele2)')</li>
          <li><code>Cage Tag</code> (Required: Existing cage number or new tag to auto-create)</li>
          <li><code>Sire Tag</code> (Optional: Physical Tag of existing sire)</li>
          <li><code>Dam Tag</code> (Optional: Physical Tag of existing dam)</li>
        </ul>
        <p>Rows with missing required data will be skipped.</p>
      </div>
    </div>
  );
}
